import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export interface ImageViewerProps {
  src: string;
  fileName: string;
  onClose: () => void;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

export const ImageViewer: React.FC<ImageViewerProps> = ({ src, fileName, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const zoomIn = () => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  const zoomOut = () => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
  const resetView = () => { setZoom(1); setOffset({ x: 0, y: 0 }); };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => {
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      return Math.min(Math.max(z + delta, MIN_ZOOM), MAX_ZOOM);
    });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    setOffset((prev) => ({
      x: prev.x + e.clientX - lastPos.current.x,
      y: prev.y + e.clientY - lastPos.current.y,
    }));
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseUp = useCallback(() => { dragging.current = false; }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-') zoomOut();
      if (e.key === '0') resetView();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const btnSx = {
    color: 'common.white',
    bgcolor: 'rgba(255,255,255,0.1)',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
  };

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 1500,
        bgcolor: 'rgba(0,0,0,0.9)',
        display: 'flex', flexDirection: 'column',
      }}
      onClick={onClose}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1, flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography sx={{ fontSize: '13px', color: 'common.white', fontWeight: 500 }}>
          {fileName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={zoomOut} sx={btnSx} aria-label="Zoom out">
            <ZoomOutIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography sx={{ fontSize: '12px', color: 'common.white', minWidth: 40, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton size="small" onClick={zoomIn} sx={btnSx} aria-label="Zoom in">
            <ZoomInIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton size="small" onClick={resetView} sx={btnSx} aria-label="Reset zoom">
            <RestartAltIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => window.open(src, '_blank', 'noopener')}
            sx={btnSx}
            aria-label="Open in new tab"
          >
            <OpenInNewIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton size="small" onClick={onClose} sx={btnSx} aria-label="Close viewer">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Image area */}
      <Box
        sx={{
          flex: 1, overflow: 'hidden', cursor: dragging.current ? 'grabbing' : 'grab',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <Box
          component="img"
          src={src}
          alt={fileName}
          draggable={false}
          sx={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transition: dragging.current ? 'none' : 'transform 0.15s ease',
            userSelect: 'none',
          }}
        />
      </Box>
    </Box>
  );
};
